import { InvalidGltfManifestError } from '@/plugins/mesh/errors'
import {
  BlobGltfBufferSource,
  DataUriGltfBufferSource,
  MemoryGltfBufferSource,
  UrlGltfBufferSource,
} from '@/plugins/mesh/loaders/gltfLowMemory/bufferSources'
import type {
  GltfBufferSource,
  GltfBufferSourceFactory,
} from '@/plugins/mesh/loaders/gltfLowMemory/interfaces'
import { resolveGltfLoadingOptions } from '@/plugins/mesh/loaders/gltfLowMemory/lowMemoryOptions'
import type { GltfManifestDocument } from '@/plugins/mesh/loaders/gltfLowMemory/types'
import type { GltfLoadingOptions, MeshLoadRequest } from '@/plugins/mesh/types'
import { getLoggerManager, LogLevel } from '@/shared/logging'

const rangeLog = getLoggerManager().getLogger('mesh:gltf:range')

/** 根据 manifest 的受控位置描述创建对应 BufferSource。 */
export class DefaultGltfBufferSourceFactory implements GltfBufferSourceFactory {
  constructor(private readonly options: GltfLoadingOptions = resolveGltfLoadingOptions()) {}

  create(
    document: GltfManifestDocument,
    bufferIndex: number,
    request: MeshLoadRequest
  ): GltfBufferSource {
    const location = document.bufferLocations.find((item) => item.bufferIndex === bufferIndex)
    if (!location) {
      throw new InvalidGltfManifestError(document.source, `buffers[${bufferIndex}] 没有来源描述`)
    }
    if (location.kind === 'data-uri') {
      return new DataUriGltfBufferSource(location.uri, location.byteLength)
    }
    if (location.kind === 'external') {
      return new UrlGltfBufferSource({
        url: location.url,
        byteLength: location.byteLength,
        headers: request.headers,
        credentials: request.credentials,
        range: this.options.range,
        budget: this.options.budget,
        onProgress: request.onProgress,
        diagnosticsEnabled: rangeLog.isEnabled(LogLevel.DEBUG),
      })
    }

    const data = document.sourceData
    if (data instanceof Blob) {
      return new BlobGltfBufferSource(
        data,
        location.byteOffset,
        location.byteLength,
        document.source
      )
    }
    if (data !== undefined && typeof data !== 'string') {
      return new MemoryGltfBufferSource(
        data,
        location.byteOffset,
        location.byteLength,
        document.source
      )
    }
    if (!request.url) {
      throw new InvalidGltfManifestError(document.source, '远程 GLB BIN 来源缺少主资产 URL')
    }
    return new UrlGltfBufferSource({
      url: request.url,
      byteLength: location.byteLength,
      baseOffset: location.byteOffset,
      headers: request.headers,
      credentials: request.credentials,
      range: this.options.range,
      budget: this.options.budget,
      onProgress: request.onProgress,
      diagnosticsEnabled: rangeLog.isEnabled(LogLevel.DEBUG),
    })
  }
}
